import { AlertTriangle, ListChecks, ExternalLink } from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { IconSectionHeader } from '@/components/shared/IconSectionHeader';
import { MonoBadge } from '@/components/shared/MonoBadge';
import { PageDescription, PanelTitle, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { SetupStatusBadge } from '@/components/setup/SetupStatusBadge';
import {
  KIND_LABEL,
  isSiteRelativeHref,
  setupStepDocsAccessibleName,
} from '@/components/setup/setup-step-meta';
import type { PluginSetupStep } from '@/lib/plugin-registry';

function MetadataRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <span className="w-28 shrink-0 text-muted-foreground">{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}

function KindSpecificDetails({ step }: { step: PluginSetupStep }) {
  const rows: React.ReactNode[] = [];

  if (step.secretKeys && step.secretKeys.length > 0) {
    const missing = new Set(step.unfulfilledKeys ?? []);
    rows.push(
      <MetadataRow key="secretKeys" label="Secret keys">
        {step.secretKeys.map((key) => {
          const isMissing = missing.has(key);
          return (
            <Badge
              key={key}
              variant={isMissing ? 'destructive' : 'secondary'}
              className="font-mono text-2xs font-normal"
            >
              {key}
              {isMissing && <span className="ml-1 opacity-70">missing</span>}
            </Badge>
          );
        })}
      </MetadataRow>,
    );
  }

  if (step.scopes && step.scopes.length > 0) {
    rows.push(
      <MetadataRow key="scopes" label="Scopes">
        {step.scopes.map((s) => (
          <MonoBadge key={s}>{s}</MonoBadge>
        ))}
      </MetadataRow>,
    );
  }

  if (step.permissions && Object.keys(step.permissions).length > 0) {
    rows.push(
      <MetadataRow key="permissions" label="Permissions">
        {Object.entries(step.permissions).map(([key, level]) => (
          <Badge key={key} variant="outline" className="font-mono text-2xs font-normal">
            {key}:{level}
          </Badge>
        ))}
      </MetadataRow>,
    );
  }

  if (step.redirectUriPath) {
    rows.push(
      <MetadataRow key="redirect" label="Redirect URI">
        <MonoBadge>{step.redirectUriPath}</MonoBadge>
      </MetadataRow>,
    );
  }

  if (step.webhookUrlPath) {
    rows.push(
      <MetadataRow key="webhook" label="Webhook URL">
        <MonoBadge>{step.webhookUrlPath}</MonoBadge>
      </MetadataRow>,
    );
  }

  if (step.verifyCommand) {
    rows.push(
      <MetadataRow key="verify" label="Verify command">
        <MonoBadge>{step.verifyCommand}</MonoBadge>
      </MetadataRow>,
    );
  }

  if (step.testEndpoint) {
    rows.push(
      <MetadataRow key="test" label="Test endpoint">
        <MonoBadge>{step.testEndpoint}</MonoBadge>
      </MetadataRow>,
    );
  }

  if (step.kind === 'external-resource' && step.requiredCount != null) {
    rows.push(
      <MetadataRow key="count" label="Resources">
        <span className="tabular-nums text-foreground/80">
          {step.resourceCount ?? 0}
          <span className="text-muted-foreground"> / {step.requiredCount} required</span>
        </span>
      </MetadataRow>,
    );
  }

  if (rows.length === 0) return null;
  return <div className="space-y-1.5">{rows}</div>;
}

function StepCard({ step }: { step: PluginSetupStep }) {
  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <PanelTitle as="h3" size="sm">
              {step.label}
            </PanelTitle>
            <Badge variant="outline" className="text-3xs font-normal">
              {KIND_LABEL[step.kind]}
            </Badge>
            <MonoBadge>{step.id}</MonoBadge>
          </div>
          {step.description && (
            <PageDescription className="mt-0 text-xs">{step.description}</PageDescription>
          )}
        </div>
        <SetupStatusBadge status={step.status} />
      </div>

      {step.message && (
        <p className="mt-3 text-xs text-status-error">
          <AlertTriangle className="mr-1 inline h-3 w-3" />
          {step.message}
        </p>
      )}

      {step.instructions && step.instructions.length > 0 && (
        <div className="mt-3">
          <SectionLabel as="span" className="mb-1.5 block text-[10px]">
            Instructions
          </SectionLabel>
          <ol className="list-decimal space-y-0.5 pl-5 text-xs text-muted-foreground">
            {step.instructions.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ol>
        </div>
      )}

      {hasMetadata(step) && (
        <div className="mt-3 border-t pt-3">
          <KindSpecificDetails step={step} />
        </div>
      )}

      {(step.docsUrl || step.adminConsoleUrl) && (
        <div className="mt-3 flex flex-wrap gap-3 border-t pt-3">
          {step.docsUrl && (
            <a
              href={step.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
              aria-label={setupStepDocsAccessibleName('Documentation', step.docsUrl)}
              title={isSiteRelativeHref(step.docsUrl) ? 'Opens in a new window' : undefined}
            >
              {!isSiteRelativeHref(step.docsUrl) && <ExternalLink className="h-3 w-3" />}
              Documentation
            </a>
          )}
          {step.adminConsoleUrl && (
            <a
              href={step.adminConsoleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              <ExternalLink className="h-3 w-3" />
              Provider console
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function hasMetadata(step: PluginSetupStep): boolean {
  return (
    (step.secretKeys?.length ?? 0) > 0 ||
    (step.scopes?.length ?? 0) > 0 ||
    (step.permissions != null && Object.keys(step.permissions).length > 0) ||
    !!step.redirectUriPath ||
    !!step.webhookUrlPath ||
    !!step.verifyCommand ||
    !!step.testEndpoint ||
    step.requiredCount != null
  );
}

export function PluginSetupStepsSection({ steps }: { steps: PluginSetupStep[] }) {
  if (steps.length === 0) return null;

  return (
    <section data-testid="plugin-detail-section-setup-steps">
      <IconSectionHeader icon={ListChecks}>Setup Steps</IconSectionHeader>
      <div className="space-y-3">
        {steps.map((step) => (
          <StepCard key={step.id} step={step} />
        ))}
      </div>
    </section>
  );
}
